import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabaseClient';
import { BACKEND_NODE_URL } from '../config/constants';
import './GasDistributorNovoPedidoModal.css';
import AppIcon from './icons/AppIcon';

function formatEnderecoLinha(end) {
  if (!end) return '';
  const parts = [
    [end.endereco, end.numero].filter(Boolean).join(', '),
    end.complemento,
    [end.bairro, end.cidade].filter(Boolean).join(' - '),
    end.cep ? `CEP ${end.cep}` : ''
  ].filter(Boolean);
  return parts.join(' — ');
}

export default function GasDistributorNovoPedidoModal({
  open,
  onClose,
  clienteId,
  projectId,
  enderecos = [],
  pedidosExistentes = [],
  onSuccess
}) {
  const [produtosCatalogo, setProdutosCatalogo] = useState([]);
  const [loadingCatalogo, setLoadingCatalogo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [allowActiveOrder, setAllowActiveOrder] = useState(false);

  const [enderecoEntrega, setEnderecoEntrega] = useState('');
  const [enderecoSelectId, setEnderecoSelectId] = useState('');
  const [formaPagamento, setFormaPagamento] = useState('PIX');
  const [horarioEntrega, setHorarioEntrega] = useState('Entrega imediata');
  const [descontoPercentual, setDescontoPercentual] = useState(0);
  const [observacoes, setObservacoes] = useState('');
  const [linhas, setLinhas] = useState([{ produto_id: '', quantidade: 1, preco_unitario: '' }]);

  const pedidosAtivos = useMemo(
    () =>
      (pedidosExistentes || []).filter((p) =>
        ['Confirmado', 'Agendado', 'Em Entrega'].includes(p.status)
      ),
    [pedidosExistentes]
  );

  useEffect(() => {
    if (!open || !projectId) return;

    const load = async () => {
      setLoadingCatalogo(true);
      setError('');
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) {
          setError('Sessão expirada. Faça login novamente.');
          return;
        }

        const res = await fetch(
          `${BACKEND_NODE_URL}/api/gas-distributor/products?projectId=${encodeURIComponent(projectId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await res.json();
        if (!res.ok) {
          setError(json.error || 'Erro ao carregar produtos');
          return;
        }
        const list = (json.data || []).filter((p) => p.ativo !== false);
        setProdutosCatalogo(list);
      } catch (e) {
        setError(e.message || 'Erro ao carregar produtos');
      } finally {
        setLoadingCatalogo(false);
      }
    };

    load();
  }, [open, projectId]);

  useEffect(() => {
    if (!open) return;
    setLinhas([{ produto_id: '', quantidade: 1, preco_unitario: '' }]);
    setEnderecoEntrega('');
    setEnderecoSelectId('');
    setFormaPagamento('PIX');
    setHorarioEntrega('Entrega imediata');
    setDescontoPercentual(0);
    setObservacoes('');
    setAllowActiveOrder(false);
    setError('');
  }, [open, clienteId]);

  const aplicarEnderecoSalvo = (id) => {
    setEnderecoSelectId(id);
    if (!id) return;
    const end = enderecos.find((e) => e.id === id);
    if (end) setEnderecoEntrega(formatEnderecoLinha(end));
  };

  const onChangeProduto = (index, produtoId) => {
    const p = produtosCatalogo.find((x) => x.id === produtoId);
    const preco = p?.preco_venda != null ? String(p.preco_venda) : '';
    setLinhas((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], produto_id: produtoId, preco_unitario: preco };
      return next;
    });
  };

  const subtotalLinha = (linha) => {
    const q = parseInt(linha.quantidade, 10) || 0;
    const u = parseFloat(String(linha.preco_unitario).replace(',', '.')) || 0;
    return q * u;
  };

  const precoTotalCalculado = useMemo(() => {
    const bruto = linhas.reduce((s, l) => s + subtotalLinha(l), 0);
    const desc = Math.max(0, Math.min(100, parseFloat(descontoPercentual) || 0));
    return bruto * (1 - desc / 100);
  }, [linhas, descontoPercentual]);

  const adicionarLinha = () => {
    setLinhas((prev) => [...prev, { produto_id: '', quantidade: 1, preco_unitario: '' }]);
  };

  const removerLinha = (index) => {
    setLinhas((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!projectId || !clienteId) {
      setError('Projeto ou cliente inválido.');
      return;
    }
    if (!enderecoEntrega.trim()) {
      setError('Informe o endereço de entrega.');
      return;
    }

    const produtosPayload = linhas
      .filter((l) => l.produto_id)
      .map((l) => {
        const q = parseInt(l.quantidade, 10) || 1;
        const pu = parseFloat(String(l.preco_unitario).replace(',', '.')) || 0;
        return {
          produto_id: l.produto_id,
          quantidade: q,
          preco_unitario: pu,
          subtotal: q * pu
        };
      });

    if (produtosPayload.length === 0) {
      setError('Adicione pelo menos um produto.');
      return;
    }

    if (pedidosAtivos.length > 0 && !allowActiveOrder) {
      setError('Existe pedido ativo. Marque a confirmação abaixo ou cancele o pedido anterior.');
      return;
    }

    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        setError('Sessão expirada.');
        return;
      }

      const res = await fetch(
        `${BACKEND_NODE_URL}/api/gas-distributor/projects/${encodeURIComponent(projectId)}/pedidos`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            cliente_id: clienteId,
            produtos: produtosPayload,
            endereco: enderecoEntrega.trim(),
            forma_pagamento: formaPagamento.trim(),
            horario_entrega: horarioEntrega.trim(),
            preco_total: Math.round(precoTotalCalculado * 100) / 100,
            desconto_percentual: parseFloat(descontoPercentual) || 0,
            observacoes: observacoes.trim() || null,
            allow_active_order: allowActiveOrder
          })
        }
      );

      const json = await res.json();
      if (!res.ok) {
        if (res.status === 409) {
          setError(json.error || 'Já existe pedido ativo para este cliente.');
        } else {
          setError(json.error || 'Erro ao criar pedido');
        }
        return;
      }

      onSuccess?.(json);
      onClose();
    } catch (err) {
      setError(err.message || 'Erro ao criar pedido');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="novo-pedido-overlay" onClick={onClose}>
      <div className="novo-pedido-modal" onClick={(ev) => ev.stopPropagation()}>
        <div className="np-header">
          <h2>Novo pedido</h2>
          <button type="button" className="btn-close-modal" onClick={onClose} aria-label="Fechar">
            <AppIcon name="close" />
          </button>
        </div>

        <form className="np-body" onSubmit={handleSubmit}>
          {pedidosAtivos.length > 0 && (
            <div className="np-warning">
              Este cliente possui {pedidosAtivos.length} pedido(s) ativo(s) ({pedidosAtivos.map((p) => p.status).join(', ')}).
              Para criar outro, marque a opção abaixo.
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                <input
                  type="checkbox"
                  checked={allowActiveOrder}
                  onChange={(e) => setAllowActiveOrder(e.target.checked)}
                />
                Criar mesmo assim (confirmo)
              </label>
            </div>
          )}

          {error && (
            <div className="np-warning" style={{ background: '#fee2e2', borderColor: '#fecaca' }}>
              {error}
            </div>
          )}

          {enderecos.length > 0 && (
            <div className="np-field">
              <label>Usar endereço salvo</label>
              <select
                value={enderecoSelectId}
                onChange={(e) => aplicarEnderecoSalvo(e.target.value)}
              >
                <option value="">— Digitar manualmente abaixo —</option>
                {enderecos.map((end) => (
                  <option key={end.id} value={end.id}>
                    {end.apelido || 'Endereço'} — {end.cidade || ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="np-field">
            <label>Endereço de entrega (texto completo)</label>
            <textarea
              rows={3}
              value={enderecoEntrega}
              onChange={(e) => setEnderecoEntrega(e.target.value)}
              placeholder="Rua, número, bairro, cidade..."
              required
            />
          </div>

          <div className="np-row-2">
            <div className="np-field">
              <label>Forma de pagamento</label>
              <input value={formaPagamento} onChange={(e) => setFormaPagamento(e.target.value)} />
            </div>
            <div className="np-field">
              <label>Horário / tipo de entrega</label>
              <input value={horarioEntrega} onChange={(e) => setHorarioEntrega(e.target.value)} />
            </div>
          </div>

          <div className="np-row-2">
            <div className="np-field">
              <label>Desconto (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={descontoPercentual}
                onChange={(e) => setDescontoPercentual(e.target.value)}
              />
            </div>
            <div className="np-field">
              <label>Total (calculado)</label>
              <input readOnly value={`R$ ${precoTotalCalculado.toFixed(2).replace('.', ',')}`} />
            </div>
          </div>

          <div className="np-field">
            <label>Observações (opcional)</label>
            <textarea rows={2} value={observacoes} onChange={(e) => setObservacoes(e.target.value)} />
          </div>

          <h3 style={{ fontSize: '0.95rem', margin: '0.75rem 0 0.25rem' }}>Produtos</h3>
          {loadingCatalogo ? (
            <p style={{ fontSize: '0.875rem', color: '#64748b' }}>Carregando catálogo...</p>
          ) : (
            <>
              {linhas.map((linha, index) => (
                <div key={index} className="np-produto-linha">
                  <div className="np-field" style={{ marginBottom: 0 }}>
                    <label>Produto</label>
                    <select
                      value={linha.produto_id}
                      onChange={(e) => onChangeProduto(index, e.target.value)}
                      required={index === 0}
                    >
                      <option value="">Selecione...</option>
                      {produtosCatalogo.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.nome} — R$ {Number(p.preco_venda).toFixed(2)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="np-field" style={{ marginBottom: 0 }}>
                    <label>Qtd</label>
                    <input
                      type="number"
                      min="1"
                      value={linha.quantidade}
                      onChange={(e) =>
                        setLinhas((prev) => {
                          const n = [...prev];
                          n[index] = { ...n[index], quantidade: parseInt(e.target.value, 10) || 1 };
                          return n;
                        })
                      }
                    />
                  </div>
                  <div className="np-field np-preco-wrap" style={{ marginBottom: 0 }}>
                    <label>Preço unit.</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={linha.preco_unitario}
                      onChange={(e) =>
                        setLinhas((prev) => {
                          const n = [...prev];
                          n[index] = { ...n[index], preco_unitario: e.target.value };
                          return n;
                        })
                      }
                    />
                  </div>
                  <button type="button" onClick={() => removerLinha(index)} title="Remover linha">
                    <AppIcon name="delete" style={{ fontSize: '20px' }} />
                  </button>
                </div>
              ))}
              <div className="np-add-prod">
                <button type="button" onClick={adicionarLinha}>
                  + Adicionar produto
                </button>
              </div>
            </>
          )}

          <div className="np-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-submit" disabled={saving || loadingCatalogo}>
              {saving ? 'Salvando...' : 'Criar pedido'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
